import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import GlassCard from '../ui/GlassCard';
import SectionHeader from '../ui/SectionHeader';
import { faqIds, discordInviteUrl } from '../../constants/content';
import { track } from '../../lib/analytics';
import { useSectionView } from '../../hooks/useSectionView';

function FAQItem({ question, answer, isOpen, onToggle, questionKey }) {
  const { darkMode } = useTheme();

  return (
    <div className={`border-b last:border-b-0 ${
      darkMode ? 'border-white/10' : 'border-gray-200'
    }`}>
      <button
        onClick={() => {
          track('faq_item_toggled', {
            question_key: questionKey,
            open: !isOpen
          });
          onToggle();
        }}
        className={`w-full flex items-center justify-between py-5 text-left transition-colors ${
          darkMode ? 'hover:text-main' : 'hover:text-purple-600'
        }`}
      >
        <span className={`titleFont text-lg font-medium pr-4 ${
          darkMode ? 'text-white' : 'text-gray-900'
        }`}>
          {question}
        </span>
        <span className={`flex-shrink-0 transition-transform duration-300 ${
          isOpen ? 'rotate-180' : ''
        }`}>
          <svg
            className={`w-5 h-5 ${darkMode ? 'text-main' : 'text-purple-600'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </span>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-96 opacity-100 pb-5' : 'max-h-0 opacity-0'
        }`}
      >
        <p className={`text-base leading-relaxed ${
          darkMode ? 'text-gray-400' : 'text-gray-600'
        }`}>
          {answer}
        </p>
      </div>
    </div>
  );
}

export default function FAQSection() {
  const { t } = useTranslation();
  const { darkMode } = useTheme();
  const sectionRef = useSectionView('faq', 9);
  const [openIndex, setOpenIndex] = useState(null);

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section
      ref={sectionRef}
      id="faq"
      className={`py-24 ${darkMode ? 'bg-main-black' : 'bg-gray-50'}`}
    >
      <div className="mainCont">
        <SectionHeader
          label={t('faq.label')}
          title={t('faq.title')}
        />

        {/* FAQ Items */}
        <GlassCard padding="lg" className="max-w-3xl mx-auto">
          {faqIds.map((id, index) => (
            <FAQItem
              key={id}
              questionKey={id}
              question={t(`faq.items.${id}.question`)}
              answer={t(`faq.items.${id}.answer`)}
              isOpen={openIndex === index}
              onToggle={() => handleToggle(index)}
            />
          ))}
        </GlassCard>

        {/* Contact link */}
        <div className="text-center mt-8">
          <p className={`text-base ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {t('faq.stillHaveQuestions')}{' '}
            <a
              href={discordInviteUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                track('cta_clicked', {
                  cta_id: 'faq_discord',
                  cta_text: t('faq.askOnDiscord'),
                  section: 'faq',
                  destination_url: discordInviteUrl,
                  destination_type: 'discord'
                });
              }}
              className={`font-medium underline transition-colors ${
                darkMode ? 'text-main hover:text-main2' : 'text-purple-600 hover:text-purple-700'
              }`}
            >
              {t('faq.askOnDiscord')}
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}
